<div class="space-y-6">

    @if (isLoading()) {
    <div class="flex items-center justify-center py-20">
        <mat-spinner diameter="36"></mat-spinner>
    </div>
    } @else if (machines().length === 0) {
    <div class="bg-white border border-slate-200 rounded-2xl shadow-sm flex flex-col items-center justify-center py-16 text-slate-400">
        <mat-icon class="!w-10 !h-10 !text-[40px] text-slate-300 mb-2">toll</mat-icon>
        <p class="text-sm font-medium">{{ 'coinMachines.none' | translate }}</p>
    </div>
    } @else {

    <!-- Machine cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        @for (m of machines(); track m.paymentTerminalId) {
        <div class="bg-white p-5 rounded-2xl border shadow-sm cursor-pointer transition-all hover:shadow-md"
             [class.border-blue-600]="selectedId() === m.paymentTerminalId"
             [class.ring-2]="selectedId() === m.paymentTerminalId"
             [class.ring-blue-100]="selectedId() === m.paymentTerminalId"
             [class.border-slate-200]="selectedId() !== m.paymentTerminalId"
             (click)="select(m)">

            <div class="flex items-start justify-between gap-3">
                <div class="flex items-center gap-3 min-w-0">
                    <div class="p-2.5 rounded-xl shrink-0"
                         [class.bg-emerald-50]="fillTone(m) === 'ok'" [class.text-emerald-600]="fillTone(m) === 'ok'"
                         [class.bg-amber-50]="fillTone(m) === 'warn'" [class.text-amber-600]="fillTone(m) === 'warn'"
                         [class.bg-rose-50]="fillTone(m) === 'full'" [class.text-rose-600]="fillTone(m) === 'full'">
                        <mat-icon>toll</mat-icon>
                    </div>
                    <div class="min-w-0">
                        <h3 class="text-sm font-bold text-slate-900 truncate">{{ m.paymentTerminalName }}</h3>
                        <p class="text-xs text-slate-500 truncate">
                            {{ m.deviceName }}@if (showSiteName() && siteName(m)) { · {{ siteName(m) }} }
                        </p>
                    </div>
                </div>
                <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-semibold shrink-0"
                      [class.bg-emerald-50]="statusTone(m) === 'online'" [class.text-emerald-700]="statusTone(m) === 'online'"
                      [class.border-emerald-200]="statusTone(m) === 'online'"
                      [class.bg-rose-50]="statusTone(m) === 'fault'" [class.text-rose-700]="statusTone(m) === 'fault'"
                      [class.border-rose-200]="statusTone(m) === 'fault'"
                      [class.bg-slate-50]="statusTone(m) === 'idle'" [class.text-slate-600]="statusTone(m) === 'idle'"
                      [class.border-slate-200]="statusTone(m) === 'idle'">
                    <span class="w-1.5 h-1.5 rounded-full"
                          [class.bg-emerald-500]="statusTone(m) === 'online'"
                          [class.bg-rose-500]="statusTone(m) === 'fault'"
                          [class.bg-slate-400]="statusTone(m) === 'idle'"></span>
                    {{ statusLabelKey(m) | translate }}
                </span>
            </div>

            <div class="mt-4 flex items-end justify-between gap-3">
                <div>
                    <p class="text-xs uppercase tracking-wider font-semibold text-slate-400">{{ 'coinMachines.inBox' | translate }}</p>
                    <p class="text-2xl font-bold text-slate-900 mt-1">
                        {{ euros(m.amountInCents) | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}
                    </p>
                </div>
                <div class="text-xs text-slate-500 text-right">
                    <p>{{ 'coinMachines.of' | translate }} {{ euros(m.capacityInCents) | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}</p>
                    <p class="mt-0.5">{{ m.coinCount ?? 0 }} {{ 'coinMachines.coins' | translate }}</p>
                </div>
            </div>

            <!-- Fill bar with the alert level marked -->
            <div class="mt-3">
                <div class="relative h-2.5 rounded-full bg-slate-100 overflow-hidden">
                    <div class="h-full rounded-full transition-all duration-500"
                         [class.bg-emerald-500]="fillTone(m) === 'ok'"
                         [class.bg-amber-400]="fillTone(m) === 'warn'"
                         [class.bg-rose-500]="fillTone(m) === 'full'"
                         [style.width.%]="(m.fillPercent ?? 0) > 100 ? 100 : (m.fillPercent ?? 0)"></div>
                    <div class="absolute top-0 bottom-0 w-0.5 bg-slate-500/60"
                         [style.left.%]="m.alertThresholdPercent"
                         [title]="'coinMachines.alertAt' | translate: { percent: m.alertThresholdPercent }"></div>
                </div>
                <div class="flex justify-between text-[11px] text-slate-500 mt-1">
                    <span>{{ 'coinMachines.fill' | translate }} {{ m.fillPercent ?? 0 }} %</span>
                    <span>{{ 'coinMachines.alertAt' | translate: { percent: m.alertThresholdPercent } }}</span>
                </div>
            </div>

            @if ((m.coins ?? []).length > 0) {
            <div class="mt-3 flex flex-wrap gap-1.5">
                @for (c of m.coins; track c.valueInCents) {
                <span class="px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-600">
                    {{ c.count }} × {{ euros(c.valueInCents) | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}
                </span>
                }
            </div>
            }

            <div class="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3">
                <div class="text-xs text-slate-500 min-w-0">
                    @if (m.lastEmptying; as last) {
                    <p class="font-semibold text-slate-600">{{ 'coinMachines.lastEmptied' | translate }}</p>
                    <p class="truncate">
                        {{ last.emptiedAt | date: 'dd.MM.yyyy HH:mm' }} ·
                        {{ euros(last.amountInCents) | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}
                    </p>
                    } @else {
                    <p>{{ 'coinMachines.neverEmptied' | translate }}</p>
                    }
                </div>
                <div (click)="$event.stopPropagation()">
                    <jjames-button variant="outline" size="sm" matIconLeft="savings"
                                   [loading]="emptyingId() === m.paymentTerminalId"
                                   [disabled]="!!emptyingId()"
                                   (clicked)="openEmptyDialog(m)">
                        {{ 'coinMachines.empty' | translate }}
                    </jjames-button>
                </div>
            </div>
        </div>
        }
    </div>

    @if (selected(); as m) {

    <!-- Chart: daily intake bars with the fill level drawn over them -->
    <div class="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm space-y-4">
        <div class="flex flex-wrap items-center justify-between gap-3 pb-2 border-b border-slate-100">
            <div>
                <h2 class="text-base font-bold text-slate-900">{{ 'coinMachines.chartTitle' | translate }} · {{ m.paymentTerminalName }}</h2>
                <p class="text-xs text-slate-500 mt-0.5">
                    {{ 'coinMachines.intake' | translate }}:
                    {{ totalIntakeInPeriod() | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}
                </p>
            </div>
            <div class="flex flex-wrap items-center gap-4">
                <div class="flex items-center gap-4 text-xs font-medium">
                    <div class="flex items-center gap-1.5">
                        <span class="w-3 h-3 rounded-xs bg-blue-200"></span>
                        <span class="text-slate-600">{{ 'coinMachines.intake' | translate }}</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <span class="w-4 h-0.5 bg-blue-600"></span>
                        <span class="text-slate-600">{{ 'coinMachines.fill' | translate }}</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <span class="w-4 border-t-2 border-dashed border-amber-500"></span>
                        <span class="text-slate-600">{{ 'coinMachines.threshold' | translate }}</span>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <span class="w-2 h-2 rounded-full bg-rose-500"></span>
                        <span class="text-slate-600">{{ 'coinMachines.emptiedMarker' | translate }}</span>
                    </div>
                </div>
                <div class="flex items-center gap-1.5 bg-slate-100 p-1 rounded-xl">
                    @for (p of periods; track p.key) {
                    <button type="button"
                            class="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all"
                            [class.bg-white]="period() === p.key"
                            [class.text-blue-700]="period() === p.key"
                            [class.shadow-xs]="period() === p.key"
                            [class.text-slate-600]="period() !== p.key"
                            (click)="setPeriod(p.key)">
                        {{ p.labelKey | translate }}
                    </button>
                    }
                </div>
            </div>
        </div>

        @if (isTrendLoading()) {
        <div class="flex items-center justify-center py-20">
            <mat-spinner diameter="36"></mat-spinner>
        </div>
        } @else if (chartColumns().length === 0) {
        <div class="flex flex-col items-center justify-center py-16 text-slate-400">
            <mat-icon class="!w-10 !h-10 !text-[40px] text-slate-300 mb-2">show_chart</mat-icon>
            <p class="text-sm font-medium">{{ 'coinMachines.noData' | translate }}</p>
        </div>
        } @else {
        <div class="relative pt-6 pb-2">
            @if (activeColumn(); as col) {
            <div class="absolute top-0 right-4 bg-slate-900 text-white text-xs px-3.5 py-2 rounded-xl shadow-lg flex flex-wrap items-center gap-3 z-10 animate-fade-in pointer-events-none">
                <div class="font-semibold text-slate-200 border-r border-slate-700 pr-3">{{ col.label }}</div>
                <div class="text-blue-300 font-bold">{{ col.amount | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }} · {{ col.coins }} {{ 'coinMachines.coins' | translate }}</div>
                <div class="text-slate-300">{{ 'coinMachines.fill' | translate }}: {{ col.fill | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }} ({{ col.fillPct }} %)</div>
                @if (col.emptied) {
                <div class="text-rose-300 font-medium">{{ 'coinMachines.emptiedMarker' | translate }}</div>
                }
            </div>
            }

            <div class="overflow-x-auto overflow-y-hidden pb-2 pt-6 -mt-6 chart-scrollbar">
                <div [style.min-width.px]="chartMinWidth()">
                    <div class="relative h-64 border-b border-slate-200">
                        <!-- Bars: one equal column per day, so the line's points sit on the column centres -->
                        <div class="absolute inset-0 grid items-end"
                             [style.grid-template-columns]="'repeat(' + chartColumns().length + ', minmax(0, 1fr))'">
                            @for (col of chartColumns(); track col.date) {
                            <div class="h-full flex items-end justify-center px-px cursor-pointer group"
                                 [class.bg-rose-50]="col.emptied"
                                 (mouseenter)="activeColumn.set(col)"
                                 (mouseleave)="activeColumn.set(null)">
                                <div class="w-full max-w-[28px] rounded-t-md bg-blue-200 group-hover:bg-blue-300 transition-colors"
                                     [style.height.%]="col.barHeightPct"></div>
                            </div>
                            }
                        </div>

                        <!-- Fill level and alert level, scaled to the capacity -->
                        <svg class="absolute inset-0 w-full h-full pointer-events-none overflow-visible"
                             [attr.viewBox]="'0 0 ' + chartColumns().length + ' 100'" preserveAspectRatio="none">
                            <line x1="0" [attr.x2]="chartColumns().length" [attr.y1]="thresholdY()" [attr.y2]="thresholdY()"
                                  stroke="#f59e0b" stroke-width="1.5" stroke-dasharray="6 4" vector-effect="non-scaling-stroke" />
                            <polyline [attr.points]="fillLinePoints()" fill="none" stroke="#2563eb" stroke-width="2.5"
                                      stroke-linejoin="round" stroke-linecap="round" vector-effect="non-scaling-stroke" />
                        </svg>
                    </div>

                    <!-- Emptying markers and day labels -->
                    <div class="grid" [style.grid-template-columns]="'repeat(' + chartColumns().length + ', minmax(0, 1fr))'">
                        @for (col of chartColumns(); track col.date) {
                        <div class="flex flex-col items-center pt-1.5 min-w-0">
                            <span class="w-2 h-2 rounded-full" [class.bg-rose-500]="col.emptied"></span>
                            @if (col.showLabel) {
                            <span class="text-[10px] text-slate-400 mt-1 whitespace-nowrap font-medium">{{ col.label }}</span>
                            }
                        </div>
                        }
                    </div>
                </div>
            </div>
        </div>
        }
    </div>

    <!-- Emptying history -->
    <div class="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
        <div class="px-5 py-4 border-b border-slate-100">
            <h2 class="text-base font-bold text-slate-900">{{ 'coinMachines.history' | translate }}</h2>
        </div>

        @if (isHistoryLoading()) {
        <div class="flex items-center justify-center py-12">
            <mat-spinner diameter="28"></mat-spinner>
        </div>
        } @else if (emptyings().length === 0) {
        <p class="px-5 py-10 text-center text-sm text-slate-400">{{ 'coinMachines.noEmptyings' | translate }}</p>
        } @else {
        <div class="overflow-x-auto">
            <table class="w-full text-sm">
                <thead class="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                    <tr>
                        <th class="px-5 py-3 text-left font-semibold">{{ 'coinMachines.colDate' | translate }}</th>
                        <th class="px-5 py-3 text-right font-semibold">{{ 'coinMachines.colAmount' | translate }}</th>
                        <th class="px-5 py-3 text-right font-semibold">{{ 'coinMachines.colCoins' | translate }}</th>
                        <th class="px-5 py-3 text-right font-semibold">{{ 'coinMachines.colRunningTotal' | translate }}</th>
                        <th class="px-5 py-3 text-left font-semibold">{{ 'coinMachines.colBy' | translate }}</th>
                        <th class="px-5 py-3 text-left font-semibold">{{ 'coinMachines.comment' | translate }}</th>
                    </tr>
                </thead>
                <tbody class="divide-y divide-slate-100">
                    @for (e of emptyings(); track e.id) {
                    <tr class="hover:bg-slate-50">
                        <td class="px-5 py-3 whitespace-nowrap text-slate-800">{{ e.emptiedAt | date: 'dd.MM.yyyy HH:mm' }}</td>
                        <td class="px-5 py-3 text-right font-semibold text-slate-900">{{ euros(e.amountInCents) | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}</td>
                        <td class="px-5 py-3 text-right text-slate-600">{{ e.coinCount }}</td>
                        <td class="px-5 py-3 text-right text-slate-600">{{ euros(e.runningTotalInCents) | currency: 'EUR' : 'symbol-narrow' : '1.2-2' }}</td>
                        <td class="px-5 py-3 text-slate-600">{{ e.emptiedByName || '—' }}</td>
                        <td class="px-5 py-3 text-slate-500 max-w-[280px] truncate" [title]="e.comment ?? ''">{{ e.comment || '—' }}</td>
                    </tr>
                    }
                </tbody>
            </table>
        </div>
        @if (emptyingsTotal() > pageSize()) {
        <div class="px-5 py-3 border-t border-slate-100">
            <jjames-paginator [config]="pagination()" (pageChange)="onPageChange($event)"></jjames-paginator>
        </div>
        }
        }
    </div>
    }
    }
</div>
